import React, { useEffect, useRef } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { palette, spaces } from '../../assets/styles/themes/tokens';
import PageContainer from '../../components/login/PageContainer';
import OnboardingHeader from '../../components/onboarding/OnboardingHeader';
import FooterLegalNavigation from '../../components/login/FooterLegalNavigation';
import useScreenSize from '../../utils/useScreenSize';
import useRouter from '../../utils/useRouter';
import { routes } from '../../constants';
import services from '../../services';
import { trackingEvents } from '../../services/tracking/trackConfig';
import { safeSessionStorage } from '../../utils/safeStorage';
import { notifyBugsnagHandledError } from '../../utils/bugsnagClient';
import { CookiebotConsentState, waitForCookiebotConsent } from '../../utils/cookiebotConsent';
import useCheckoutStatusPolling, {
  CheckoutPollState,
  CheckoutPurchase,
} from './useCheckoutStatusPolling';

// The onboarding flow (OnboardingPage, which reads the token as optInToken) lives at
// the top-level `/konto-erstellen` — the same target the onboarding email links to.
// NB: this is NOT `kontoErstellenPath` ('/registrierung/konto-erstellen'), which is
// the premium-registration PaymentPage.
const onboardingPath = `/${routes.onboardingPage.root}`;

// Deliberately keyed under SM-142 (the parent EDD of SM-312) — the plan pins the
// key format `sm142_purchase_fired:<session_id>`.
export const PURCHASE_FIRED_KEY_PREFIX = 'sm142_purchase_fired:';

interface StateCopy {
  title: string;
  subtitle: string;
  body?: string;
}

// Static, terminal "the email still has your link" copy — also shown when no
// session_id is present (nothing to poll) and on timeout.
const EMAIL_FALLBACK_BODY =
  'We have sent you a link to complete the creation of your account. Please follow the link in the email.';

// While polling we prepare the user's session; both in-progress states share the
// same "setting up" copy, and `ready` is the success state that unlocks the button.
const isLoadingState = (state: CheckoutPollState) =>
  state === 'confirmingPayment' || state === 'settingUpAccount';

const getStateCopy = (state: CheckoutPollState): StateCopy => {
  switch (state) {
    case 'confirmingPayment':
    case 'settingUpAccount':
      return { title: 'Setting up your account…', subtitle: 'Please wait a moment' };
    case 'ready':
      return { title: 'You\'re all set – let\'s go!', subtitle: 'Payment successful' };
    case 'existingAccount':
      return {
        title: 'Welcome back to meinUnterricht!',
        subtitle: 'Your Premium subscription is now active.',
        body: 'You can use your Premium subscription right away.',
      };
    case 'expired':
      return {
        title: 'Your checkout session has expired',
        subtitle: 'Please start the checkout process again.',
      };
    case 'sessionNotFound':
      return {
        title: 'We couldn\'t find your checkout session',
        subtitle: 'Please check your email inbox',
        body: EMAIL_FALLBACK_BODY,
      };
    case 'checkoutStatusFailed':
      return {
        title: 'We couldn\'t confirm your payment',
        subtitle: 'Please check your email inbox',
        body: EMAIL_FALLBACK_BODY,
      };
    case 'emailFallback':
    default:
      return {
        title: 'Payment successful',
        subtitle: 'Please check your email inbox',
        body: EMAIL_FALLBACK_BODY,
      };
  }
};

// Mirrors the legacy Zuora flow's buildRegisterPremiumTrackingPayload shape —
// everything nested under `other`, since registerPremiumFinish.requiredData is
// ['other']. `priceId` replaces the legacy `licenceModelId`; `session_id` (the
// GA4 event-id DLV) and `is_trial` are new keys the GTM container maps (SM-312).
const buildPurchasePushPayload = (
  sessionId: string,
  purchase: CheckoutPurchase,
  userId: string | null,
  consentState: CookiebotConsentState
) => ({
  other: {
    user_id: userId ?? undefined,
    order_value: purchase.orderValue,
    currency: purchase.currency,
    discount_code: purchase.discountCode,
    session_id: sessionId,
    is_trial: purchase.isTrial,
    consent_state: consentState,
    subscription: {
      priceId: purchase.priceId,
      paymentMethod: purchase.paymentMethod,
      paymentPeriod: purchase.paymentPeriod,
    },
  },
});

const CheckoutSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const { isXsScreen, isSmScreen } = useScreenSize();
  const { router, match } = useRouter();
  const sessionId = match.location.query.session_id?.trim() || undefined;

  const { state, email, onboardingToken, purchase, userId } = useCheckoutStatusPolling(sessionId);
  const { title, subtitle, body } = getStateCopy(state);

  // Fire the `register_premium_finish` push exactly once per successful checkout
  // (SM-312): the ref dedupes per session id within this mount (a changed
  // session_id may register again), the sessionStorage key survives reloads.
  // `purchase` is best-effort on the backend — when it is absent we skip the
  // push entirely (never partial data) and only log; the page UX is unaffected.
  // The push waits (bounded) for Cookiebot to report a known consent state:
  // this document is fresh after Stripe's redirect, and pushing before consent
  // restores makes the GA4 tag fire cookieless, losing the conversion's
  // attribution. On timeout the push fires anyway — never lost. The dedupe key
  // is set at push time (not at `ready`) so a reload during the wait
  // re-attempts instead of swallowing the event; the pending wait is
  // window-scoped, so navigating away does not cancel the push.
  const purchasePushAttemptedRef = useRef<string | null>(null);
  useEffect(() => {
    if (state !== 'ready' || !sessionId || purchasePushAttemptedRef.current === sessionId) return;
    purchasePushAttemptedRef.current = sessionId;

    if (!purchase) {
      notifyBugsnagHandledError(
        new Error(`register_premium_finish skipped: no purchase block on checkout status (session ${sessionId})`)
      );
      return;
    }

    const dedupeKey = `${PURCHASE_FIRED_KEY_PREFIX}${sessionId}`;
    if (safeSessionStorage.getItem(dedupeKey)) return;

    waitForCookiebotConsent()
      .then((consentState) => {
        if (safeSessionStorage.getItem(dedupeKey)) return;
        safeSessionStorage.setItem(dedupeKey, '1');
        return services.track.eventTrack(
          trackingEvents.registerPremiumFinish,
          buildPurchasePushPayload(sessionId, purchase, userId, consentState)
        );
      })
      .catch((error: unknown) => {
        notifyBugsnagHandledError(
          error instanceof Error
            ? error
            : new Error(`register_premium_finish push failed (session ${sessionId})`)
        );
      });
  }, [state, sessionId, purchase, userId]);

  const isLoading = isLoadingState(state);
  const isReady = state === 'ready';
  const isExistingAccount = state === 'existingAccount';

  const handleCreateProfile = () => {
    if (!onboardingToken) return;
    router.replace(`${onboardingPath}?token=${encodeURIComponent(onboardingToken)}`);
  };

  // SM-345: returning/reactivated buyers already have a profile — send them to
  // the existing login instead of profile creation.
  const handleLogIn = () => {
    router.replace(`/${routes.login.root}`);
  };

  return (
    <PageContainer pageBodySx={{ gap: 0 }}>
      <Box
        data-testid='checkout-success-page'
        data-state={state}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: isXsScreen ? spaces.xl : spaces.xxxl,
        }}
      >
        <OnboardingHeader title={t(title)} subtitle={t(subtitle)} hideIcon />
        {isLoading && (
          <CircularProgress
            data-testid='checkout-success-spinner'
            sx={{ '& .MuiCircularProgress-svg': { width: 'auto', height: 'auto' } }}
          />
        )}
        {isReady && (
          <CheckCircleIcon
            data-testid='checkout-success-check'
            sx={{
              fontSize: 64,
              color: palette.success.main,
              animation: 'checkmark-pop 0.35s ease-out',
              '@keyframes checkmark-pop': {
                from: { transform: 'scale(0)', opacity: 0 },
                to: { transform: 'scale(1)', opacity: 1 },
              },
            }}
          />
        )}
        {body && (
          <Typography
            variant={isSmScreen ? 'body2' : 'body1'}
            sx={{
              color: palette.text.secondary,
              textAlign: 'center',
              marginTop: spaces.md,
            }}
          >
            {t(body)}
          </Typography>
        )}
        {/* A returning buyer is being sent to the login, not to an inbox — echoing the
            address back only invites confusion. The email-fallback states still need it:
            there, checking that inbox is the instruction. */}
        {email && !isExistingAccount && (
          <Typography
            data-testid='checkout-success-email'
            variant={isSmScreen ? 'body2' : 'body1'}
            sx={{ color: palette.text.secondary, textAlign: 'center' }}
          >
            {t('Confirmation sent to:')}{' '}
            <Box component='span' sx={{ fontWeight: 'bold', color: palette.text.primary }}>
              {email}
            </Box>
          </Typography>
        )}
        {(isLoading || isReady) && (
          <Button
            data-testid='checkout-success-create-profile'
            variant='contained'
            disabled={!isReady}
            onClick={handleCreateProfile}
            fullWidth
          >
            {t('Create profile')}
          </Button>
        )}
        {isExistingAccount && (
          <Button
            data-testid='checkout-success-log-in'
            variant='contained'
            onClick={handleLogIn}
            fullWidth
          >
            {t('Get started now')}
          </Button>
        )}
      </Box>
      <FooterLegalNavigation isSmallScreen={isXsScreen} />
    </PageContainer>
  );
};

export default CheckoutSuccessPage;
